import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../../core/firebase/firebase';
import { ERPUser } from '../../../core/auth/types';
import { ExtraSupplierContact, SupplierRecord, SupplierStatus } from '../types/supplierTypes';

const suppliersCol = collection(db, 'suppliers');

function makeSupplierCode() {
  return `SUP-${Math.floor(100000 + Math.random() * 900000)}`;
}

function normalizeContacts(raw: unknown): ExtraSupplierContact[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item: any) => ({
    name: String(item?.name || ''),
    phone: String(item?.phone || ''),
    email: String(item?.email || '')
  }));
}

export function emptySupplier(): Partial<SupplierRecord> {
  return {
    supplierName: '',
    supplierType: 'hotel',
    department: 'package',
    primaryContactName: '',
    phone: '',
    tollFreeNo: '',
    email: '',
    city: '',
    country: '',
    paymentTerms: '',
    status: 'active',
    extraContacts: [],
    branchOfficeName: '',
    branchOfficeAddress: '',
    bankDetails: '',
    notes: '',
    internalNotes: '',
    website: '',
    address: ''
  };
}

export function normalizeSupplier(id: string, raw: Record<string, any>): SupplierRecord {
  return {
    id,
    supplierCode: String(raw.supplierCode || makeSupplierCode()),
    supplierName: String(raw.supplierName || raw.companyName || ''),
    supplierType: String(raw.supplierType || raw.type || 'other'),
    department: String(raw.department || raw.departments?.[0] || 'general'),
    primaryContactName: String(raw.primaryContactName || raw.contactPerson || ''),
    phone: String(raw.phone || raw.phoneFull || ''),
    tollFreeNo: String(raw.tollFreeNo || ''),
    email: String(raw.email || ''),
    city: String(raw.city || ''),
    country: String(raw.country || ''),
    paymentTerms: String(raw.paymentTerms || ''),
    status: (raw.status || 'active') as SupplierStatus,
    extraContacts: normalizeContacts(raw.extraContacts),
    branchOfficeName: String(raw.branchOfficeName || ''),
    branchOfficeAddress: String(raw.branchOfficeAddress || ''),
    bankDetails: String(raw.bankDetails || ''),
    notes: String(raw.notes || ''),
    internalNotes: String(raw.internalNotes || ''),
    website: String(raw.website || ''),
    address: String(raw.address || ''),
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    createdBy: String(raw.createdBy || ''),
    updatedBy: String(raw.updatedBy || '')
  };
}

export async function listSuppliers(): Promise<SupplierRecord[]> {
  const snapshot = await getDocs(query(suppliersCol, orderBy('updatedAt', 'desc')));
  return snapshot.docs.map((item) => normalizeSupplier(item.id, item.data()));
}

export async function findSupplierDuplicates(form: Partial<SupplierRecord>): Promise<SupplierRecord[]> {
  const all = await listSuppliers();
  const name = String(form.supplierName || '').trim().toLowerCase();
  const phone = String(form.phone || '').trim();
  const email = String(form.email || '').trim().toLowerCase();

  return all.filter((supplier) =>
    (name && supplier.supplierName.toLowerCase() === name) ||
    (phone && supplier.phone === phone) ||
    (email && supplier.email.toLowerCase() === email)
  );
}

export async function saveSupplier(form: Partial<SupplierRecord>, user: ERPUser) {
  const payload = {
    supplierCode: form.supplierCode || makeSupplierCode(),
    supplierName: form.supplierName || '',
    supplierType: form.supplierType || 'other',
    department: form.department || 'general',
    primaryContactName: form.primaryContactName || '',
    phone: form.phone || '',
    tollFreeNo: form.tollFreeNo || '',
    email: form.email || '',
    city: form.city || '',
    country: form.country || '',
    paymentTerms: form.paymentTerms || '',
    status: form.status || 'active',
    extraContacts: Array.isArray(form.extraContacts) ? form.extraContacts : [],
    branchOfficeName: form.branchOfficeName || '',
    branchOfficeAddress: form.branchOfficeAddress || '',
    bankDetails: form.bankDetails || '',
    notes: form.notes || '',
    internalNotes: form.internalNotes || '',
    website: form.website || '',
    address: form.address || '',
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    createdBy: form.createdBy || user.uid
  };

  if (form.id) {
    await updateDoc(doc(db, 'suppliers', form.id), payload);
    return form.id;
  }

  const ref = await addDoc(suppliersCol, { ...payload, createdAt: serverTimestamp() });
  return ref.id;
}

export async function deleteSupplier(id: string) {
  return deleteDoc(doc(db, 'suppliers', id));
}
